/* global fetch */

import React from "react";

import { connect } from "react-redux";
import get from "lodash/get";
import PropTypes from "prop-types";
import { ResponsiveHeroImage, LazyLoadImages, UpdateOnInterval, SocialShare, AccessType } from "@quintype/components";
import { StoryHeaderCard } from "../story-page-components/story-header-card.js";
import { StoryPageCard } from "../story-page-components/story-page-card.js";
import { LiveBlogKeyEvents } from "../story-page-components/live-blog-key-events.js";
import TimeAgo from "react-timeago";
import format from "date-fns/format";
import { MetypeCommentingWidget } from "@metype/components";
import { breakpoint } from "../../../utils/breakpoint";
import { addTargetBlankToExtLinks } from "../../../utils/add-target-blank-to-extlinks";
import { altTagOfHeroImages, getHorizontalSocialShare, getPrimaryColor, formatter, isEmpty } from "../../utils";
import { getSocialShareTemplate } from "../story-page-components/social-share/get-social-template";
import { getAccessData, handleAccess, checkIfStoryPublic } from "../sso-components/sso-utils";

class LiveBlogTemplate extends React.Component {
  constructor(props) {
    super(props);
    const metypeConfig = get(props, ["config", "theme-attributes", "metype-config"], {});
    const cdnImage = get(props, ["config", "cdn-image"], "");
    this.metypeConfig = metypeConfig;
    this.cdnImage = cdnImage;
  }

  componentDidMount() {
    this.props.initAccessType();
    const { checkAccess, story, accessUpdated, accessIsLoading, config } = this.props;
    getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config);
  }

  formatter(value, unit, suffix, date, defaultFormatter) {
    return format(new Date(date), "DD MMM, hh:mm A");
  }

  generateHostUrl(story = {}) {
    if (global.location) {
      return `${global.location.origin}/${story.slug}`;
    }
  }

  loadWidget() {
    if (this.props.preview || isEmpty(this.metypeConfig)) {
      return null;
    }
    return (
      <MetypeCommentingWidget
        host={this.metypeConfig.host}
        accountId={parseInt(this.metypeConfig.accountId)}
        pageURL={this.generateHostUrl(this.props.story)}
        primaryColor={this.metypeConfig.primaryColor}
      />
    );
  }
  hideBrokenImg(ev) {
    ev.target.style.display = "none";
    ev.currentTarget.style.padding = "10px";
  }

  getStoryCardTemplate = (cards = []) => {
    const socialShareStyle = getHorizontalSocialShare(this.props.config);
    const primaryColor = getPrimaryColor(this.props.config);

    return cards.map((card, index) => (
      <div className="story-cards" id={card.id} key={index}>
        <div className="live-blog-social-share-wrapper">
          <TimeAgo date={card["card-added-at"]} className="card-added-at" formatter={this.formatter} />
          <SocialShare
            url={encodeURIComponent(`${this.props.story.slug}?cardId=${card.id}`)}
            title={card.metadata ? card.metadata["social-share"].title : this.props.story.title}
            template={getSocialShareTemplate(socialShareStyle)}
            primaryColor={primaryColor}
            hashtags={get(this.props, ["story", "tags"], [])
              .map(tag => tag.name)
              .toString()}
          />
        </div>
        <StoryPageCard key={index} card={card} story={this.props.story} cdn={this.cdnImage} />
        <p className="card-updated-at">
          Last updated{" "}
          <TimeAgo
            date={card["card-updated-at"]}
            formatter={(value, unit, suffix, date) => formatter(value, unit, suffix, date, this.props.config)}
          />
        </p>
        {this.props.story.cards.length - 1 === index && this.loadWidget()}
      </div>
    ));
  };

  render() {
    const storyAccess = get(this.props, ["access"]);
    const accessLoading = get(this.props, ["accessLoading"]);
    const isStoryPublic = checkIfStoryPublic(this.props.story);
    const storyCards = get(this.props, ["story", "cards"], []);

    return (
      <article className="live-blog-story blank-story">
        <figure
          onError={ev => this.hideBrokenImg(ev)}
          className="live-blog-story-hero-image blank-story-hero-image qt-responsive-image"
        >
          <LazyLoadImages>
            {breakpoint("tablet") ? (
              <ResponsiveHeroImage
                story={this.props.story}
                aspectRatio={[16, 9]}
                alt={altTagOfHeroImages(this.props.story)}
                defaultWidth={480}
                widths={[250, 480, 640]}
                sizes="(max-width: 500px) 98%, (max-width: 768px) 48%, 98%"
                imgParams={{ auto: ["format", "compress"], fit: "max" }}
              />
            ) : (
              <ResponsiveHeroImage
                story={this.props.story}
                aspectRatio={[8, 3]}
                alt={altTagOfHeroImages(this.props.story)}
                defaultWidth={480}
                widths={[250, 480, 640, 960, 1200]}
                sizes="(max-width: 500px) 98%"
                imgParams={{ auto: ["format", "compress"], fit: "max" }}
              />
            )}
          </LazyLoadImages>
        </figure>
        <div className={`live-blog-story__extra-wrapper`}>
          <div className="live-blog-story--wrapper blank-story--wrapper">
            <StoryHeaderCard story={this.props.story} config={this.props.config} />
          </div>
          <LiveBlogKeyEvents story={this.props.story} />
          <div className="live-blog-story--wrapper blank-story--wrapper">
            <div className="live-blog-story--content blank-story--content">
              {isStoryPublic
                ? this.getStoryCardTemplate(storyCards)
                : handleAccess(this.props.story, storyAccess, accessLoading, this.props.config, cards =>
                    this.getStoryCardTemplate(cards)
                  ) /* Method to check for access of the user */}
            </div>
          </div>
        </div>
      </article>
    );
  }
}

LiveBlogTemplate.propTypes = {
  preview: PropTypes.bool,
  story: PropTypes.object,
  config: PropTypes.object,
  checkAccess: PropTypes.func,
  initAccessType: PropTypes.func,
  accessUpdated: PropTypes.func,
  accessIsLoading: PropTypes.func
};

class LiveBlogStoryBase extends React.Component {
  componentDidMount() {
    addTargetBlankToExtLinks(this.storyWrapperElement);
  }

  getStoryData = async () => {
    const storyId = get(this.props, ["story", "id"]);
    const url = `/api/v1/stories/${storyId}`;
    const response = await (await fetch(url)).json();
    return response;
  };

  render() {
    const email = get(this.props, ["member", "email"]);
    const phone = get(this.props, ["member", "metadata", "phone-number"], "");
    const accessTypeKey = get(this.props, ["config", "theme-attributes", "accesstypeKey"]);
    const disableMetering = get(this.props, ["config", "theme-attributes", "disableMetering"]) === "true";
    const isStaging = get(this.props, ["config", "theme-attributes", "accesstypeEnvironment"]) === "staging";
    const enableAccesstype = get(this.props, ["config", "theme-attributes", "enableAccesstype"]) === "true";

    return (
      <AccessType
        enableAccesstype={enableAccesstype}
        isStaging={isStaging}
        accessTypeKey={accessTypeKey}
        email={email}
        phone={phone}
        disableMetering={disableMetering}
      >
        {({ initAccessType, checkAccess, accessUpdated, accessIsLoading }) => (
          <div className={`story-grid`} ref={el => (this.storyWrapperElement = el)}>
            <UpdateOnInterval dataLoader={this.getStoryData} interval={20000} initData={{ story: this.props.story }}>
              {({ data }) => {
                const clonedProps = Object.assign({}, this.props, data);
                return (
                  <LiveBlogTemplate
                    accessIsLoading={accessIsLoading}
                    accessUpdated={accessUpdated}
                    initAccessType={initAccessType}
                    checkAccess={checkAccess}
                    {...clonedProps}
                  />
                );
              }}
            </UpdateOnInterval>
          </div>
        )}
      </AccessType>
    );
  }
}

LiveBlogStoryBase.propTypes = {
  story: PropTypes.object
};

const mapStateToProps = state => ({
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true)
});

export const LiveBlogStory = connect(mapStateToProps)(LiveBlogStoryBase);
